import React, { useCallback, useState, useMemo, useEffect } from "react";
import { LayoutPage } from "../../layoutPage";
import { Box, ThemeProvider } from "@mui/material";
import { GridCellParams, GridPinnedColumnFields } from "@mui/x-data-grid-premium";
import Loader from "../../../components/loader/loader";
import Preferences from "../../../components/preferences/prefereneces";
import { DEFAULT_VIEW_NAME, ROUTES } from "../../../utils/constants";
import { PREF_BUYERS } from "../../../api/concierges/preferences";
import "./buyerListing.scss";
import HomeSiteFullDetails from "../homeSiteDetailsModal/HomeSiteFullDetails";
import ConciergeFilterComponent from "../concierge/ConciergeFilterComponent";
import TabsComponent from "./buyerListingTabs";
import DataGridPremiumComponent from "./buyerListingDataGrid";
import { BuyerListingDropdown } from "./buyerListingDropdown";
import { useBuyerListingData } from "./useBuyerListingData";
import { theme } from "../../../components/gridToolbarComponents/gridToolbarComponents";
import { MilestoneFilterDropDown } from "../../../components/milestoneFilterDropdown";
import { defaultPinned, pinnedSessionKey, sanitizePinned } from "../../../utils/pinning";
import { useDivisionFeatureFlags } from "../../../stateContext/featureFlags/FeatureFlagProvider";
import { registerMilestoneRowClickHandler, wasMilestoneRowClickHandled } from "../../../components/core/homesites/homesiteColumns";


export const BuyerListing: React.FC = () => {

  const {
    userInfo,
    isLoadingHomeSites,
    counts,
    applyDynamicFilters,
    enrichedRows,
    hsColumns,
    defaultNonVisibleColumns,
    setSelectedMetric,
    getParams,
    setGetParams,
    optionsTabs,
    buyerNameField,
    selectedTab,
    setSelectedTab,
    setColumnVisibilityModel,
    columnVisibilityModel,
    curColumns,
    columnVisibilityModelTabs,
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    orderedColumns,
    columnReordered,
    setOrderedColumns,
    isLoadingPreference,
    divisionMilestones,
    milestoneSpecificCounts,
    selectedMilestone,
    selectedViewFromBuyerListing,
    setSelectedViewFromBuyerListing
  } = useBuyerListingData();

  const { showMilestoneProgress } = useDivisionFeatureFlags(userInfo?.userDivCode || "");

  const [informationModal, setInformationModal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clickedField, setClickedField] = useState<string | null>(null);
  const [activeMilestoneName, setActiveMilestoneName] = useState<string | undefined>(undefined);

  const handleCellClick = useCallback((params: GridCellParams) => {
    if (params.field === "homeSiteId" || params.field === "numExceptions" || params.field === "numAlerts" || params.field === "numNotes") {
      setInformationModal(params.row);
      setClickedField(params.field);
      setActiveMilestoneName(undefined);
      setShowModal(true);
    } else if (params.field === "milestoneProgress" && !wasMilestoneRowClickHandled()) {
      const breakdown: { name: string; completed: number; total: number; progress: number }[] =
        params.row.milestoneProgressBreakdown || [];
      const respectiveMilestone =
        breakdown.find((m) => m.progress > 0 && m.progress < 100)?.name ||
        breakdown[0]?.name ||
        undefined;
      setInformationModal(params.row);
      setClickedField(params.field);
      setActiveMilestoneName(respectiveMilestone);
      setShowModal(true);
    }
  }, []);

  useEffect(() => {
    registerMilestoneRowClickHandler((row, milestoneName) => {
      setInformationModal(row);
      setClickedField("milestoneProgress");
      setActiveMilestoneName(milestoneName);
      setShowModal(true);
    });
    return () => registerMilestoneRowClickHandler(null);
  }, []);

  const LoaderWrapper = useMemo(
    () =>
      ({ isLoading, children }: { isLoading: boolean; children: React.ReactNode }) =>
        isLoading ? <Loader /> : <>{children}</>,
    []
  );

  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(defaultPinned(showMilestoneProgress));

  // Rehydrate only from session
  useEffect(() => {
    const viewName = selectedViewFromBuyerListing || DEFAULT_VIEW_NAME;
    const key = pinnedSessionKey(PREF_BUYERS, viewName);
    const fromSession = sessionStorage.getItem(key);
    if (fromSession) {
      try {
        setPinnedColumns(sanitizePinned(JSON.parse(fromSession), showMilestoneProgress));
      } catch (err) {
        console.error("Error parsing pinned columns from sessionStorage:", err);
      }
    }
  }, [selectedViewFromBuyerListing, showMilestoneProgress]);

  const handlePinnedColumnsChange = useCallback(
    (next: GridPinnedColumnFields) => {
      const sanitized = sanitizePinned(next, showMilestoneProgress);
      setPinnedColumns(sanitized);

      // Save session-only
      const viewName = selectedViewFromBuyerListing || DEFAULT_VIEW_NAME;
      const key = pinnedSessionKey(PREF_BUYERS, viewName);
      sessionStorage.setItem(key, JSON.stringify(sanitized));
    },
    [selectedViewFromBuyerListing, showMilestoneProgress]
  );

  return (
    <ThemeProvider theme={theme}>
      <LayoutPage title={ROUTES.closings.buyerListing.label}>
        <p></p>
      </LayoutPage>
      <Box sx={{ height: "100%", width: "100%", backgroundColor: "#fff", padding: "16px", borderRadius: "8px" }}>
        <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end", marginBottom: "16px" }}>
          <ConciergeFilterComponent applyDynamicFilters={applyDynamicFilters} />
          <BuyerListingDropdown
            counts={counts}
            setSelectedMetric={setSelectedMetric}
            getParams={getParams}
            setGetParams={setGetParams}
          />
          <MilestoneFilterDropDown
            milestones={divisionMilestones?.data || []}
            setGetParams={setGetParams}
            getParams={getParams}
            buyerlistingData={enrichedRows}
            milestoneSpecificCounts={milestoneSpecificCounts}
          />
          {selectedTab.value === 0 && (
            <>
              <Preferences
                page={PREF_BUYERS}
                email={userInfo?.email}
                columns={hsColumns}
                orderedColumns={orderedColumns}
                columnReordered={columnReordered}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                setOrderedColumns={setOrderedColumns}
                defaultNonVisibleColumns={defaultNonVisibleColumns}
                selectedViewFromBuyerListing={selectedViewFromBuyerListing}
                setSelectedViewFromBuyerListing={setSelectedViewFromBuyerListing}
                pinnedColumns={pinnedColumns}
                setPinnedColumns={setPinnedColumns}
              />

            </>
          )}
        </Box>
        <LoaderWrapper isLoading={isLoadingHomeSites || isLoadingPreference}>
          <Box>
            <TabsComponent options={optionsTabs} selectedTab={selectedTab} onTabChange={setSelectedTab} />
            <DataGridPremiumComponent
              rows={enrichedRows}
              columns={curColumns}
              onCellClick={handleCellClick}
              columnVisibilityModel={columnVisibilityModel}
              columnOrder={orderedColumns}
              handleColumnVisibilityChange={handleColumnVisibilityChange}
              selectedTab={selectedTab}
              columnVisibilityModelTabs={columnVisibilityModelTabs}
              handleColumnOrderChange={(p) => handleColumnOrderChange(p, pinnedColumns)}
              handleCellClick={handleCellClick}
              theme={theme}
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={handlePinnedColumnsChange}
            />
          </Box>
        </LoaderWrapper>

        {showModal && informationModal && (
          <HomeSiteFullDetails
            userId={userInfo?.email}
            title={informationModal?.[buyerNameField] || ""}
            setShowModal={(val) => {
              setShowModal(val);
              if (!val) setActiveMilestoneName(undefined);
            }}
            setInformationModal={setInformationModal}
            homeSiteDetails={informationModal}
            milestoneFilter={selectedMilestone || undefined}
            expandMilestone={activeMilestoneName}
            tab={{
              homeSiteId: "additional",
              numExceptions: "exceptions",
              numAlerts: "alerts",
              numNotes: "notes",
              milestoneProgress: "basic",
            }[clickedField as string] || "additional"}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};
